import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTrail, animated, useSpring } from '@react-spring/web';
import api from "../api/axios";
import Toast from "../components/Toast";
import { useToast } from "../hooks/useToast";
import UserForm from "../components/UserForm";
import SearchFilter from "../components/SearchFilter";
import BulkActions from "../components/BulkActions";
import ExportData from "../components/ExportData";
import StatusBadge from "../components/StatusBadge";
import { exportSingleRecord } from "../utils/pdfExport";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PageTransition from '../components/PageTransition';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function UserManagement() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const { toasts, success, error: showError, removeToast } = useToast();

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  // Animation hooks - must be at top level
  const trail = useTrail(currentUsers.length, {
    from: { opacity: 0, transform: 'translate3d(0,40px,0)' },
    to: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const headerSpring = useSpring({
    from: { opacity: 0, transform: 'translate3d(0,-50px,0)' },
    to: { opacity: 1, transform: 'translate3d(0,0px,0)' },
    config: { tension: 300, friction: 30 }
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersRes, bookingsRes] = await Promise.all([
        api.get("/users"),
        api.get("/bookings")
      ]);

      const usersData = usersRes.data.data || usersRes.data || [];
      const bookings = bookingsRes.data.data || bookingsRes.data || [];

      const usersWithBookings = usersData.map(user => {
        const userBookings = bookings.filter(booking => booking.user_id === user.id);
        const activeBooking = userBookings.find(booking => booking.status === 'active' || booking.status === 'confirmed');
        const completedBookings = userBookings.filter(booking => booking.status === 'completed');

        return {
          ...user,
          bookings: userBookings,
          activeBooking: activeBooking || null,
          bookingStatus: activeBooking ? activeBooking.status : 'no_booking',
          completed_bookings_count: completedBookings.length
        };
      });

      setUsers(usersWithBookings);
      setFilteredUsers(usersWithBookings);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (searchTerm) => {
    const filtered = users.filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.activeBooking?.property?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleFilter = (filterValue) => {
    if (!filterValue) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => {
        if (filterValue === 'active_rental') return user.bookingStatus === 'active' || user.bookingStatus === 'confirmed';
        if (filterValue === 'no_booking') return user.bookingStatus === 'no_booking';
        if (filterValue === 'cancelled') return user.bookings.some(b => b.status === 'cancelled');
        if (filterValue === 'completed') return user.bookings.some(b => b.status === 'completed');
        return user.status === filterValue;
      });
      setFilteredUsers(filtered);
    }
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_user'))) {
      try {
        if (!id || (typeof id !== 'number' && typeof id !== 'string')) {
          throw new Error('Invalid user ID');
        }
        const numericId = Number(id);
        if (isNaN(numericId) || numericId <= 0) {
          throw new Error('Invalid user ID format');
        }
        const sanitizedId = encodeURIComponent(String(numericId));
        await api.delete(`/users/${sanitizedId}`);
        success(t('user_deleted_success'));
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        showError(t('failed_delete_user'));
      }
    }
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (action === 'delete') {
      if (window.confirm(`${t('delete')} ${selectedIds.length} ${t('selected')} ${t('users')}?`)) {
        try {
          const validIds = selectedIds.filter(id => {
            const numericId = Number(id);
            return !isNaN(numericId) && numericId > 0;
          });
          await Promise.all(validIds.map(id => {
            const sanitizedId = encodeURIComponent(String(Number(id)));
            return api.delete(`/users/${sanitizedId}`);
          }));
          success(`${validIds.length} ${t('users')} ${t('deleted_successfully')}!`);
          fetchUsers();
          setSelectedUsers([]);
        } catch (error) {
          console.error('Bulk delete failed:', error);
          showError(t('failed_delete_users'));
        }
      }
    } else if (action === 'activate') {
      try {
        const validIds = selectedIds.filter(id => {
          const numericId = Number(id);
          return !isNaN(numericId) && numericId > 0;
        });
        await Promise.all(validIds.map(id => {
          const sanitizedId = encodeURIComponent(String(Number(id)));
          return api.put(`/users/${sanitizedId}`, { status: 'active' });
        }));
        success(`${validIds.length} ${t('users')} ${t('activated_successfully')}!`);
        fetchUsers();
        setSelectedUsers([]);
      } catch (error) {
        console.error('Bulk activate failed:', error);
        showError(t('failed_activate_users'));
      }
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setShowForm(true);
  };

  const handleFormSave = async () => {
    setShowForm(false);
    setEditUser(null);
    await fetchUsers();
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      const numericId = Number(id);
      if (isNaN(numericId) || numericId <= 0) {
        throw new Error('Invalid user ID');
      }
      const sanitizedId = encodeURIComponent(String(numericId));
      await api.put(`/users/${sanitizedId}`, { status });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };

  const bulkActions = [
    { key: 'delete', label: t('delete'), className: 'bg-red-500 text-white hover:bg-red-600' },
    { key: 'activate', label: t('activate'), className: 'bg-green-500 text-white hover:bg-green-600' }
  ];

  const getBookingStatusBadge = (user) => {
    if (user.activeBooking) {
      const booking = user.activeBooking;
      const statusColors = {
        'active': 'bg-green-100 text-green-800',
        'confirmed': 'bg-blue-100 text-blue-800',
        'pending': 'bg-yellow-100 text-yellow-800',
        'cancelled': 'bg-red-100 text-red-800',
        'completed': 'bg-gray-100 text-gray-800'
      };

      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[booking.status] || 'bg-gray-100 text-gray-800'}`}>
          {booking.status === 'active' ? t('currently_renting') :
            booking.status === 'confirmed' ? t('currently_renting') :
              booking.status === 'pending' ? t('pending') :
                booking.status === 'cancelled' ? t('cancelled') :
                  booking.status === 'completed' ? t('completed') : booking.status}
        </span>
      );
    }
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{t('no_booking_yet')}</span>;
  };

  const statusFilters = [
    { value: 'active', label: t('active') },
    { value: 'inactive', label: t('inactive') },
    { value: 'active_rental', label: t('currently_renting') },
    { value: 'no_booking', label: t('not_booked_yet') },
    { value: 'cancelled', label: t('cancelled') },
    { value: 'completed', label: t('completed') }
  ];

  if (loading) {
    return (
      <PageTransition>
        <div className={`flex items-center justify-center min-h-96 ${isDark ? 'bg-gray-900' : ''}`}>
          <LoadingSpinner variant="users" size="large" />
        </div>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <PageTransition>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-8 text-center`}
        >
          <div className={`text-6xl mb-4 ${isDark ? 'text-gray-400' : 'text-red-500'}`}>
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            {t('error_loading_users')}
          </h2>
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            {error}
          </p>
          <button
            onClick={fetchUsers}
            className={`px-6 py-2 rounded-lg transition-colors ${isDark ? 'bg-gray-600 text-gray-100 hover:bg-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {t('try_again')}
          </button>
        </motion.div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        {/* Header */}
        <animated.div
          style={headerSpring}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}
              >
                {t('tenant_customer_management')}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
              >
                {t('manage_tenant_data')}
              </motion.p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ExportData data={filteredUsers} filename="users" title={t('users_management_report')} includeStats={true} language={language} />
              </motion.div>
              <AnimatePresence>
                {selectedUsers.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      const selectedData = filteredUsers.filter(u => selectedUsers.includes(u.id));
                      const doc = new jsPDF();
                      doc.setFontSize(16);
                      doc.text(t('selected_users_export'), 20, 20);
                      doc.setFontSize(10);
                      doc.text(`${t('generated')}: ${new Date().toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US')}`, 20, 30);

                      const headers = [t('id'), t('name'), t('email'), t('phone'), t('status')];
                      const rows = selectedData.map(item => [
                        item.id || '-',
                        item.name || '-',
                        item.email || '-',
                        item.phone || '-',
                        item.status || '-'
                      ]);

                      autoTable(doc, {
                        head: [headers],
                        body: rows,
                        startY: 40
                      });

                      const fileName = selectedData.length === 1
                        ? `${selectedData[0].name.replace(/[^a-zA-Z0-9]/g, '_')}_${selectedData[0].id}_${new Date().toISOString().split('T')[0]}.pdf`
                        : `selected_${selectedData.length}_users_${new Date().toISOString().split('T')[0]}.pdf`;
                      doc.save(fileName);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm shadow-lg hover:shadow-xl text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700' : 'bg-gradient-to-r from-blue-600 to-blue-700'
                      }`}
                  >
                    <motion.i
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                      className="fas fa-file-pdf mr-1"
                    ></motion.i>
                    Export Selected ({selectedUsers.length})
                  </motion.button>
                )}
              </AnimatePresence>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowForm(true)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  }`}
              >
                <motion.i
                  animate={{ rotate: [0, 90, 0] }}
                  transition={{ duration: 0.3 }}
                  className="fas fa-plus mr-2"
                ></motion.i>
                {t('add_user')}
              </motion.button>
            </motion.div>
          </div>
        </animated.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col lg:flex-row gap-4"
        >
          <motion.div
            className="flex-1"
            whileHover={{ scale: 1.01 }}
          >
            <SearchFilter
              onSearch={handleSearch}
              onFilter={handleFilter}
              filters={statusFilters}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="flex items-center gap-2"
          >
            <span className="text-sm text-gray-600">{t('view')}:</span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'cards'
                  ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-600 to-blue-700'} text-white shadow-lg`
                  : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`
                }`}
            >
              <i className="fas fa-th-large mr-1"></i> {t('cards')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded-lg text-sm transition-all ${viewMode === 'table'
                  ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-600 to-blue-700'} text-white shadow-lg`
                  : `${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`
                }`}
            >
              <i className="fas fa-table mr-1"></i> {t('table')}
            </motion.button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <BulkActions
            selectedItems={selectedUsers}
            onAction={handleBulkAction}
            actions={bulkActions}
          />
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {viewMode === 'cards' ? (
            <motion.div
              key="cards"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {currentUsers.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className={`relative ${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300 flex flex-col ${index % 2 === 0 ? (isDark ? 'bg-gray-800/95' : 'bg-white/95') : (isDark ? 'bg-gray-700/95' : 'bg-slate-50/95')}`}
                >
                  {/* Checkbox and Status Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <motion.input
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserSelect(user.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                    >
                      <StatusBadge
                        currentStatus={user.status}
                        statusOptions={['active', 'inactive']}
                        onStatusChange={(newStatus) => handleStatusUpdate(user.id, newStatus)}
                        labels={{ 'active': 'Aktif', 'inactive': 'Diblokir' }}
                      />
                    </motion.div>
                  </div>
                  <div className="flex justify-center mb-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                      {user.profile_photo ? (
                        <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-white text-2xl font-bold">{user.name.charAt(0).toUpperCase()}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-center mb-4 flex-1">
                    <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{user.name}</h3>
                    <p className={`text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</p>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>ID: {user.id}</p>
                    <div className="mt-4">
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className={`text-sm mb-1 ${isDark ? 'text-blue-400' : 'text-blue-600'} font-medium`}
                      >
                        <motion.i
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                          className="fas fa-calendar-check mr-1"
                        ></motion.i>
                        {user.bookings_count || 0} {(user.bookings_count || 0) === 1 ? 'booking' : 'bookings'}
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className={`text-xs mb-2 ${isDark ? 'text-green-400' : 'text-green-600'} font-medium`}
                      >
                        <motion.i
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 3, repeat: Infinity, repeatDelay: 5 }}
                          className="fas fa-check-circle mr-1"
                        ></motion.i>
                        {user.completed_bookings_count || 0} selesai
                      </motion.p>
                      {user.activeBooking && (
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {user.activeBooking.property?.name || 'Property Info Not Available'}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.activeBooking.property?.city} • {user.activeBooking.start_date} - {user.activeBooking.end_date}
                          </div>
                          {getBookingStatusBadge(user)}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Actions */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex gap-2 justify-center mt-auto"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(user)}
                      className={`py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                        }`}
                    >
                      <motion.i
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 0.5 }}
                        className="fas fa-edit mr-1"
                      ></motion.i>
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        try {
                          exportSingleRecord(user, 'user', 'user');
                        } catch (error) {
                          console.error('Single export failed:', error);
                          showError('Export gagal: ' + error.message);
                        }
                      }}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                        }`}
                      title="Export User Details"
                    >
                      <motion.i
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
                        className="fas fa-file-pdf"
                      ></motion.i>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: -5 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(user.id)}
                      className="bg-gradient-to-r from-gray-500 to-gray-600 text-white py-2 px-3 rounded-lg text-sm font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <motion.i
                        whileHover={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 0.3 }}
                        className="fas fa-trash"
                      ></motion.i>
                    </motion.button>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border overflow-hidden`}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`bg-gradient-to-r ${isDark ? 'from-gray-700 to-gray-600' : 'from-slate-50 to-blue-50'}`}>
                    <tr>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Penyewa</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Email</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Total Bookings</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Booking Status</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Status</th>
                      <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentUsers.map((user, index) => (
                      <tr key={user.id} className={`transition-all duration-200 ${isDark ? `hover:bg-gray-700 ${index % 2 === 0 ? 'bg-gray-800/50' : 'bg-gray-700/50'}` : `hover:bg-blue-50 ${index % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50'}`}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mr-3">
                              {user.profile_photo ? (
                                <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                              ) : (
                                <i className="fas fa-user text-blue-600"></i>
                              )}
                            </div>
                            <div>
                              <div className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{user.name}</div>
                              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ID: {user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{user.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.35 }}
                            className="flex flex-col gap-1"
                          >
                            <motion.span
                              whileHover={{ scale: 1.05 }}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${(user.bookings_count || 0) > 0
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}
                            >
                              <motion.i
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                className="fas fa-calendar-check mr-1"
                              ></motion.i>
                              {user.bookings_count || 0} total
                            </motion.span>
                            <motion.span
                              whileHover={{ scale: 1.05 }}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            >
                              <motion.i
                                animate={{ rotate: [0, 360] }}
                                transition={{ duration: 3, repeat: Infinity, repeatDelay: 5 }}
                                className="fas fa-check-circle mr-1"
                              ></motion.i>
                              {user.completed_bookings_count || 0} selesai
                            </motion.span>
                          </motion.div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            {user.activeBooking ? (
                              <div>
                                <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                  {user.activeBooking.property?.name || 'Property Info Not Available'}
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {user.activeBooking.property?.city || 'Location'} • {user.activeBooking.start_date} - {user.activeBooking.end_date}
                                </div>
                                {getBookingStatusBadge(user)}
                              </div>
                            ) : (
                              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>-</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 }}
                            whileHover={{ scale: 1.05 }}
                          >
                            <StatusBadge
                              currentStatus={user.status}
                              statusOptions={['active', 'inactive']}
                              onStatusChange={(newStatus) => handleStatusUpdate(user.id, newStatus)}
                              labels={{ 'active': 'Akun Aktif', 'inactive': 'Akun Diblokir' }}
                            />
                          </motion.div>
                        </td>
                        <td className="px-6 py-4">
                          <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 }}
                            className="flex gap-1"
                          >
                            <motion.button
                              whileHover={{ scale: 1.05, y: -1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleEdit(user)}
                              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                                }`}
                            >
                              Edit
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => {
                                try {
                                  exportSingleRecord(user, 'user', 'user');
                                } catch (error) {
                                  console.error('Single export failed:', error);
                                  showError('Export gagal: ' + error.message);
                                }
                              }}
                              className={`px-2 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md text-white ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                                }`}
                            >
                              <motion.i
                                animate={{ y: [0, -1, 0] }}
                                transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
                                className="fas fa-file-pdf"
                              ></motion.i>
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1, rotate: -5 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDelete(user.id)}
                              className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2 py-1 rounded-lg text-xs font-medium hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              Delete
                            </motion.button>
                          </motion.div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        <AnimatePresence>
          {filteredUsers.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}
            >
              <div className={`text-6xl mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <i className="fas fa-users"></i>
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                No Users Found
              </h3>
              <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                Try adjusting your search or filter criteria
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredUsers.length}
          />
        )}

        <AnimatePresence>
          {showForm && (
            <UserForm
              user={editUser}
              onSave={handleFormSave}
              onCancel={() => { setShowForm(false); setEditUser(null); }}
            />
          )}
        </AnimatePresence>

        {/* Toast Notifications */}
        <AnimatePresence>
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              isVisible={true}
              onClose={() => removeToast(toast.id)}
              duration={toast.duration}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </PageTransition>
  );
}